import React, { useEffect, useState } from "react";
import { portalApi, formatPortalError } from "@/api/portalClient";
import { Loader2, Calendar, Clock, MapPin, Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SHIFT_TYPE_LABELS = {
  regular: { label: "Säännöllinen", cls: "bg-white/10 text-white/80" },
  overtime: { label: "Ylityö", cls: "bg-amber-500/15 text-amber-300" },
  sunday: { label: "Sunnuntai (+100%)", cls: "bg-purple-500/15 text-purple-300" },
  evening: { label: "Ilta (+15-20%)", cls: "bg-blue-500/15 text-blue-300" },
  night: { label: "Yö (+30-50%)", cls: "bg-indigo-500/15 text-indigo-300" },
  holiday: { label: "Pyhä", cls: "bg-red-500/15 text-red-300" },
};
const STATUS_LABELS = {
  scheduled: { label: "Suunniteltu", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  in_progress: { label: "Käynnissä", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  completed: { label: "Suoritettu", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  approved: { label: "Hyväksytty", cls: "bg-green-500/15 text-green-300 border-green-500/30" },
  cancelled: { label: "Peruutettu", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
};

const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const monthAhead = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };

export default function EmployeePortalShifts() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(monthAgo());
  const [endDate, setEndDate] = useState(monthAhead());

  const load = async () => {
    setLoading(true);
    try {
      const r = await portalApi.get(`/employee-portal/me/shifts?start_date=${startDate}&end_date=${endDate}`);
      setShifts(r.data || []);
    } catch (e) { toast.error(formatPortalError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const totalHours = shifts.reduce((a, s) => a + (parseFloat(s.hours) || 0), 0);

  return (
    <div className="space-y-4" data-testid="emp-portal-shifts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display gradient-text-luxe">Omat työvuorot</h1>
          <p className="text-xs text-white/40 mt-1">Yhteensä {totalHours.toFixed(2)} h valitulla aikavälillä</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-white/60">Alkaen</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 bg-black/30 border-white/10 h-9 w-40" data-testid="shifts-start" />
          </div>
          <div>
            <Label className="text-xs text-white/60">Päättyy</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 bg-black/30 border-white/10 h-9 w-40" data-testid="shifts-end" />
          </div>
          <Button onClick={load} className="btn-luxe h-9" data-testid="shifts-filter">Hae</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-white/40"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
      ) : shifts.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/40">
          <Calendar className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">Ei työvuoroja valitulla aikavälillä</p>
        </div>
      ) : (
        <div className="space-y-2">
          {shifts.map(s => {
            const ty = SHIFT_TYPE_LABELS[s.shift_type] || SHIFT_TYPE_LABELS.regular;
            const st = STATUS_LABELS[s.status] || STATUS_LABELS.scheduled;
            return (
              <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04] transition" data-testid={`shift-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-display text-white">{s.title || s.job_name || s.route_name || "Työvuoro"}</h3>
                      <Badge className={`${ty.cls} text-[10px] border-0`}>{ty.label}</Badge>
                      <Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge>
                    </div>
                    <p className="text-xs text-white/60 flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3" />
                      {s.start_at?.slice(0, 10)} klo <span className="font-mono">{s.start_at?.slice(11, 16)} – {s.end_at?.slice(11, 16)}</span>
                      {s.break_minutes > 0 && <span className="text-white/40">· tauot {s.break_minutes} min</span>}
                    </p>
                    {s.location && <p className="text-xs text-white/50 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {s.location}</p>}
                    {s.job_name && <p className="text-xs text-white/50 flex items-center gap-1 mt-1"><Briefcase className="w-3 h-3" /> {s.job_name}</p>}
                    {s.notes && <p className="text-xs text-white/60 mt-2 italic">{s.notes}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-white/40">Tehoaikoja</p>
                    <p className="text-2xl font-display text-emerald-300 font-mono">{s.hours} h</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
